import { getToken, authFetch, authWrite } from '@/lib/auth';
import { normalizePlantingLogs } from '@/lib/plantingLogNormalization';

const STORAGE_KEY = 'garden-planting-logs';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PlantingLog {
  id: string;
  plantId: string;
  plantName: string;
  plantedDate: string; // ISO date string
  location?: string;
  notes?: string;
  status: 'growing' | 'harvesting' | 'finished' | 'dormant';
  quantity: number; // defaults to 1
  createdAt: string;
  templateId?: string; // set when the log was created from a garden template
  established?: boolean; // true if plant was already growing when logged
  variety?: string;
}

// ── Normalization helpers ─────────────────────────────────────────────────────

function normalizeLogs(logs: PlantingLog[]): PlantingLog[] {
  const shapeNormalized = logs.map(log => ({
    ...log,
    quantity: log.quantity || 1,
    variety: typeof log.variety === 'string' && log.variety.trim()
      ? log.variety
      : undefined,
  }));
  return normalizePlantingLogs(shapeNormalized).logs;
}

// ── Local storage helpers ─────────────────────────────────────────────────────

function loadLocal(): PlantingLog[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PlantingLog[]) : [];
  } catch {
    return [];
  }
}

function saveLocal(logs: PlantingLog[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(logs)); } catch {}
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function getPlantingLogs(): Promise<PlantingLog[]> {
  if (!getToken()) return [];
  try {
    const res = await authFetch('/api/user/planting-logs');
    if (!res.ok) {
      const local = normalizeLogs(loadLocal());
      saveLocal(local);
      return local;
    }
    const data = await res.json() as { logs?: PlantingLog[] };
    if (!Array.isArray(data.logs)) {
      const local = normalizeLogs(loadLocal());
      saveLocal(local);
      return local;
    }
    const normalized = normalizeLogs(data.logs);
    saveLocal(normalized);
    return normalized;
  } catch {
    const local = normalizeLogs(loadLocal());
    saveLocal(local);
    return local;
  }
}

export async function savePlantingLogs(logs: PlantingLog[]): Promise<void> {
  saveLocal(normalizeLogs(logs));
  if (!getToken()) return;

  // Guard: never overwrite non-empty KV data with an empty array
  const existingCache = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY + '-prev');
      return raw ? (JSON.parse(raw) as unknown[]) : [];
    } catch { return []; }
  })();
  if (logs.length === 0 && existingCache.length > 0) {
    console.warn('[plantingTracker] Prevented writing empty array over existing data');
    return;
  }

  try {
    const normalized = normalizeLogs(logs);
    await authWrite(
      '/api/user/planting-logs',
      {
        method: 'PUT',
        body: JSON.stringify({ logs: normalized }),
      },
      'Your plant log changes were saved locally, but could not be synced.',
    );
    try { localStorage.setItem(STORAGE_KEY + '-prev', JSON.stringify(normalized)); } catch {}
  } catch {}
}

export async function addPlantingLog(log: PlantingLog): Promise<void> {
  const current = await getPlantingLogs();
  await savePlantingLogs([...current, log]);
}

export async function updatePlantingLog(id: string, updates: Partial<PlantingLog>): Promise<void> {
  const current = await getPlantingLogs();
  await savePlantingLogs(current.map(l => l.id === id ? { ...l, ...updates } : l));
}

export async function removePlantingLog(id: string): Promise<void> {
  const current = await getPlantingLogs();
  await savePlantingLogs(current.filter(l => l.id !== id));
}
